import * as THREE from "three";
import { COLORS } from "../config";
import { GraphicsSettingsManager } from "../config/graphics";

const materialCache = new Map<string, THREE.Material>();

function shouldRenderEdges(forceRender: boolean): boolean {
  if (forceRender) {
    return true;
  }

  const settings = GraphicsSettingsManager.getInstance().getCurrentSettings();
  return settings.quality !== "low";
}

function getMaterialCacheKey(
  baseColor: number,
  shaderEnabled: boolean,
  outlineColor: number,
  outlineWidth: number,
): string {
  return `${baseColor}-${shaderEnabled}-${outlineColor}-${outlineWidth}`;
}

// --- Helper: Dodawanie krawędzi do obiektu ---
export function addEdges(object: THREE.Mesh, edgeColor: number = COLORS.edge, forceRender: boolean = false): void {
  if (!shouldRenderEdges(forceRender)) {
    return;
  }

  if (!object.geometry) {
    console.warn("Object missing geometry, cannot add edges:", object);
    return;
  }
  try {
    const edges = new THREE.EdgesGeometry(object.geometry);
    const lineMaterial = new THREE.LineBasicMaterial({ color: edgeColor });
    const lineSegments = new THREE.LineSegments(edges, lineMaterial);
    object.add(lineSegments);
    object.userData.edges = lineSegments;
  } catch (error) {
    console.error("Error creating edges for object:", object, error);
  }
}

// --- Zmodyfikowana funkcja tworzenia materiału ---
// Teraz przyjmuje flagę, czy użyć shadera
export function createPaperMaterial(
  baseColor: number,
  useShader: boolean = true,
  outlineColor: number = 0x000000,
  outlineWidth: number = 1.5,
  forceShader: boolean = false,
): THREE.Material {
  const settings = GraphicsSettingsManager.getInstance().getCurrentSettings();
  const shaderEnabled = forceShader || (useShader && settings.quality !== "low");
  const cacheKey = getMaterialCacheKey(baseColor, shaderEnabled, outlineColor, outlineWidth);
  const cachedMaterial = materialCache.get(cacheKey);
  if (cachedMaterial) {
    return cachedMaterial;
  }

  const material = shaderEnabled
    ? new THREE.ShaderMaterial({
        uniforms: {
          baseColor: { value: new THREE.Color(baseColor) },
          outlineColor: { value: new THREE.Color(outlineColor) },
          outlineWidth: { value: outlineWidth },
          lightDirection: { value: new THREE.Vector3(0.5, 0.8, 0.2).normalize() },
        },
        vertexShader: vertexShader,
        fragmentShader: fragmentShader,
        glslVersion: THREE.GLSL1,
      })
    : new THREE.MeshLambertMaterial({
        color: baseColor,
        flatShading: true,
      });

  materialCache.set(cacheKey, material);
  return material;
}

export function createTent(position: THREE.Vector3, scene: THREE.Scene): THREE.Group {
  const tentGroup = new THREE.Group();
  tentGroup.position.copy(position);

  const tentHeight = 2.5;
  const tentRadius = 2.0;
  const tentGeo = new THREE.ConeGeometry(tentRadius, tentHeight, 4); // Square base for tent
  const tentMat = createPaperMaterial(0xd2a679); // Warm tan color for tent
  const tentMesh = new THREE.Mesh(tentGeo, tentMat);
  tentMesh.position.y = tentHeight / 2;
  tentMesh.castShadow = true;
  addEdges(tentMesh);
  tentGroup.add(tentMesh);

  scene.add(tentGroup);
  return tentGroup;
}

// Note: This function requires a createTank function to be imported or defined
export function createStaticTank(
  position: THREE.Vector3,
  rotationY: number,
  scene: THREE.Scene,
  createTank: () => THREE.Group,
): THREE.Group {
  const staticTank = createTank();
  staticTank.position.copy(position);
  staticTank.rotation.y = rotationY;
  scene.add(staticTank);
  return staticTank;
}

// --- Helper: Dodawanie współrzędnych barycentrycznych do geometrii ---
export function addBarycentricCoordinates(geometry: THREE.BufferGeometry): void {
  const positionAttribute = geometry.getAttribute("position");
  if (!positionAttribute) {
    console.error("Geometry must have a position attribute.");
    return;
  }

  const vertexCount = positionAttribute.count;
  const barycentricCoords: number[] = [];

  // Dla każdego trójkąta (3 wierzchołki) dodajemy wzory (1,0,0), (0,1,0), (0,0,1)
  for (let i = 0; i < vertexCount; i++) {
    const indexInTriangle = i % 3;
    if (indexInTriangle === 0) {
      barycentricCoords.push(1, 0, 0);
    } else if (indexInTriangle === 1) {
      barycentricCoords.push(0, 1, 0);
    } else {
      barycentricCoords.push(0, 0, 1);
    }
  }

  geometry.setAttribute("barycentric", new THREE.Float32BufferAttribute(barycentricCoords, 3));
}

// --- Definicje Shaderów ---

const vertexShader = `
  attribute vec3 barycentric; // Odbieramy atrybut barycentryczny
  varying vec3 vBarycentric;  // Przekazujemy go do fragment shadera (zostanie zinterpolowany)
  varying vec3 vNormal;       // Przekazujemy normalną do obliczeń światła
  varying vec3 vViewPosition; // Przekazujemy pozycję w przestrzeni widoku

  void main() {
    vBarycentric = barycentric; // Przypisz do varying
    vec4 modelViewPosition = modelViewMatrix * vec4(position, 1.0);
    vViewPosition = -modelViewPosition.xyz; // Wektor od wierzchołka do kamery
    vNormal = normalize(normalMatrix * normal); // Transformuj normalną
    gl_Position = projectionMatrix * modelViewPosition; // Standardowa transformacja pozycji
  }
`;

const fragmentShader = `
  uniform vec3 baseColor;      // Kolor bazowy obiektu
  uniform vec3 outlineColor;   // Kolor konturu
  uniform float outlineWidth;  // Jak "grube" mają być linie (np. 0.02)
  uniform vec3 lightDirection; // Uproszczony kierunek światła (znormalizowany)

  varying vec3 vBarycentric;   // Zinterpolowane współrzędne barycentryczne
  varying vec3 vNormal;        // Zinterpolowana normalna
  varying vec3 vViewPosition;  // Zinterpolowana pozycja w przestrzeni widoku

  // Funkcja do obliczenia minimalnej wartości współrzędnej barycentrycznej
  float edgeFactor() {
    // fwidth() daje przybliżoną szerokość piksela, pomaga uzyskać stałą grubość linii
    vec3 d = fwidth(vBarycentric);
    // smoothstep tworzy płynne przejście zamiast ostrej linii
    vec3 a3 = smoothstep(vec3(0.0), d * outlineWidth, vBarycentric);
    // Zwraca minimalną wartość - im bliżej krawędzi, tym bliżej 0
    return min(min(a3.x, a3.y), a3.z);
  }

  void main() {
    float edge = edgeFactor();

    // Proste cieniowanie Lamberta (światło kierunkowe)
    float dotNL = max(0.0, dot(normalize(vNormal), normalize(lightDirection)));
    vec3 LambertDiffuse = baseColor * (0.3 + 0.7 * dotNL); // Dodanie światła ambient + kierunkowego

    // Jeśli jesteśmy blisko krawędzi (edge bliskie 0), użyj koloru konturu, w przeciwnym razie koloru bazowego z cieniowaniem
    gl_FragColor = vec4(mix(outlineColor, LambertDiffuse, edge), 1.0);
  }
`;

export function createPapercraftTank(position: THREE.Vector3, scene: THREE.Scene, tankModel: any): THREE.Group {
  const tankGroup = new THREE.Group();
  tankGroup.position.copy(position);

  // Tank body - using original dimensions
  const bodyGeometry = new THREE.BoxGeometry(
    tankModel.dimensions.body.width,
    tankModel.dimensions.body.height,
    tankModel.dimensions.body.length,
  );
  const bodyMaterial = createPaperMaterial(tankModel.colors.body);
  const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
  body.position.y = 0.6; // Original position
  body.castShadow = true;
  body.receiveShadow = true;
  body.userData.type = "body";
  addEdges(body);
  tankGroup.add(body);

  // Tank tracks - using original dimensions
  const trackGeometry = new THREE.BoxGeometry(
    tankModel.dimensions.tracks.width,
    tankModel.dimensions.tracks.height,
    tankModel.dimensions.tracks.length,
  );
  const trackMaterial = createPaperMaterial(tankModel.colors.tracks);

  const leftTrack = new THREE.Mesh(trackGeometry, trackMaterial);
  leftTrack.position.set(-1.2, 0.2, 0); // Original position
  leftTrack.castShadow = true;
  leftTrack.receiveShadow = true;
  leftTrack.userData.type = "track";
  addEdges(leftTrack);
  tankGroup.add(leftTrack);

  const rightTrack = new THREE.Mesh(trackGeometry, trackMaterial);
  rightTrack.position.set(1.2, 0.2, 0); // Original position
  rightTrack.castShadow = true;
  rightTrack.receiveShadow = true;
  rightTrack.userData.type = "track";
  addEdges(rightTrack);
  tankGroup.add(rightTrack);

  // Tank turret - using original dimensions
  const turretGeometry = new THREE.CylinderGeometry(
    tankModel.dimensions.turret.radius,
    tankModel.dimensions.turret.radius + 0.2,
    tankModel.dimensions.turret.height,
    16,
  );
  const turretMaterial = createPaperMaterial(tankModel.colors.turret);
  const turret = new THREE.Mesh(turretGeometry, turretMaterial);
  turret.position.y = 1.1; // Original position
  turret.rotation.y = 0;
  turret.castShadow = true;
  turret.receiveShadow = true;
  turret.userData.type = "turret";
  addEdges(turret);
  tankGroup.add(turret);

  // Tank antenna - from original
  const antennaGeometry = new THREE.CylinderGeometry(0.02, 0.02, 1, 8);
  const antenna = new THREE.Mesh(antennaGeometry, turretMaterial);
  antenna.position.set(0.5, 0.75, 0);
  antenna.userData.type = "antenna";
  addEdges(antenna);
  turret.add(antenna);

  // Tank barrel - using original dimensions
  const barrelGeometry = new THREE.CylinderGeometry(
    tankModel.dimensions.barrel.radius,
    tankModel.dimensions.barrel.radius,
    tankModel.dimensions.barrel.length,
    8,
  );
  const barrelMaterial = createPaperMaterial(tankModel.colors.barrel);
  const barrel = new THREE.Mesh(barrelGeometry, barrelMaterial);
  barrel.rotation.x = -Math.PI / 2; // Original rotation
  barrel.position.set(0, 0, tankModel.dimensions.barrel.length / 2);
  barrel.castShadow = true;
  barrel.userData.type = "barrel";
  addEdges(barrel);
  turret.add(barrel);

  // Tank wheels - using original dimensions and details
  // Reduced segments for performance (12 instead of 16)
  const wheelGeometry = new THREE.CylinderGeometry(0.2, 0.2, 0.4, 12);
  const wheelMaterial = createPaperMaterial(0x333333);
  wheelGeometry.rotateZ(Math.PI / 2);

  // Add wheel details from original
  const wheelDetailGeometry = new THREE.BoxGeometry(0.05, 0.05, 0.3);
  const detailMaterial = createPaperMaterial(0x222222);

  // Create wheels with details as in original
  const frontLeftWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
  frontLeftWheel.position.set(-1, 0.2, -1.5);
  frontLeftWheel.castShadow = true;
  frontLeftWheel.receiveShadow = true;
  frontLeftWheel.userData.type = "wheel";
  addEdges(frontLeftWheel);

  const frontLeftDetail = new THREE.Mesh(wheelDetailGeometry, detailMaterial);
  frontLeftDetail.position.set(0, 0.15, 0);
  frontLeftWheel.add(frontLeftDetail);
  tankGroup.add(frontLeftWheel);

  const frontRightWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
  frontRightWheel.position.set(1, 0.2, -1.5);
  frontRightWheel.castShadow = true;
  frontRightWheel.receiveShadow = true;
  frontRightWheel.userData.type = "wheel";
  addEdges(frontRightWheel);

  const frontRightDetail = new THREE.Mesh(wheelDetailGeometry, detailMaterial);
  frontRightDetail.position.set(0, 0.15, 0);
  frontRightWheel.add(frontRightDetail);
  tankGroup.add(frontRightWheel);

  const backLeftWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
  backLeftWheel.position.set(-1, 0.2, 1.5);
  backLeftWheel.castShadow = true;
  backLeftWheel.receiveShadow = true;
  backLeftWheel.userData.type = "wheel";
  addEdges(backLeftWheel);
  tankGroup.add(backLeftWheel);

  const backRightWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
  backRightWheel.position.set(1, 0.2, 1.5);
  backRightWheel.castShadow = true;
  backRightWheel.receiveShadow = true;
  backRightWheel.userData.type = "wheel";
  addEdges(backRightWheel);
  tankGroup.add(backRightWheel);

  return tankGroup;
}
